import * as React from "react";

import { Loading } from "../components/common";

import Survey from "../models/Survey";
import Layer from "../models/Layer";
import Asset from "../models/Asset";
import Annotation from "../models/Annotation";
import AdHoc from "../models/AdHoc";
import Cannot from "../models/Cannot";

import ApiSink from "../api/sink";
import ApiLayers from "../api/layers";
import ApiAnnotations from "../api/annotations";

import UndoArchiveManager from "../services/UndoArchiveManager";
import { useAuthActions } from "./auth";
import useRefreshOnFocus from "../hooks/useRefreshOnFocus";
import { useNavigate } from "react-router-dom";
import { useCallback, useMemo } from "react";
import { SinkState } from "../interfaces";

export type SurveyDateType =
  | "flight-date"
  | "locked-date"
  | "created-date"
  | "submitted-date"
  | "completed-date";

export type SurveyListFilters = {
  searchString: string | null;
  selectedTagIds: number[];
  selectedFolderIds: number[];
  selectedSiteIds: number[];
  selectedStatuses: string[];
  selectedOrderTypes: string[];
  dateRange: { startDate: string; endDate: string };
  dateType: SurveyDateType;
  selectedCreatedByUserIds: number[];
  selectedLockedByUserIds: number[];
  showOnlyArchived: boolean;
};

const defaultSurveyListFilters: SurveyListFilters = {
  searchString: null,
  selectedTagIds: [],
  selectedFolderIds: [],
  selectedSiteIds: [],
  selectedStatuses: [],
  selectedOrderTypes: [],
  dateRange: { startDate: "", endDate: "" },
  dateType: "created-date",
  selectedCreatedByUserIds: [],
  selectedLockedByUserIds: [],
  showOnlyArchived: false,
};

interface IPortalState {
  loading: boolean;
  sink: SinkState;
  surveys: Survey[];
  layers: Layer[];
  assets: Asset[];
  annotations: Annotation[];
  selectedSurveyId: number | null;
  focusedSurveyId: number | null;
  selectedAnnotationId: number | null;
  focusedAnnotationId: number | null;
  selectedModelId: number | null;
  inspectorModel: any;
  inspectorShowMaterial: number;
  openSurveyIds: number[];
  selectedAssetId: number | null;
  focusedAssetIds: number[];
  isCenterPaneCollapsed: boolean;
  surveyListFilters: SurveyListFilters;
  undoArchiveQueue: { type: string; id: number }[];
  allowAnnotationArrowKeyNavigation: boolean;
  surveyListExpansion: Record<number, boolean>;
  surveyListDownloadsOpen: Record<number, boolean>;
  surveyListScrollPosition: number;
}

interface ISinkActions {
  resetUiState: () => void;
  unsetSelectedSurvey: () => void;
  setSelectedModel: (id: number | null) => void;
  setSelectedAsset: (assetId: number) => void;
  setCenterPaneCollapsed: (isCollapsed: boolean) => void;
  setSurveySearchString: (surveySearchString: string) => void;
  setSurveyDateRange: (d: { startDate?: string; endDate?: string }) => void;
  setSelectedTagIds: (selectedTagIds: number[]) => void;
  setSelectedFolderIds: (selectedFolderIds: number[]) => void;
  setSelectedSiteIds: (selectedSiteIds: number[]) => void;
  setSelectedStatuses: (selectedStatuses: string[]) => void;
  setSelectedOrderTypes: (selectedOrderTypes: string[]) => void;
  setSurveyDateType: (surveyDateType: SurveyDateType) => void;
  setSelectedCreatedByUserIds: (selectedCreatedByUserIds: number[]) => void;
  setSelectedLockedByUserIds: (selectedLockedByUserIds: number[]) => void;
  setShowOnlyArchived: (showOnlyArchived: boolean) => void;
  stopInspecting: () => void;
  setAllowAnnotationArrowKeyNavigation: (
    allowAnnotationArrowKeyNavigation: boolean
  ) => void;
  setSelectedSurveyId: (id: number | null) => void;
  resink: () => Promise<void>;
  getLayers: (surveyId: number) => Promise<void>;
  toggleSurveyExpansion: (surveyId: number) => void;
  expandAllSurveys: (surveyIds: number[]) => void;
  collapseAllSurveys: () => void;
  toggleSurveyListDownloadsOpen: (surveyId: number) => void;
  closeSurveyListDownloadsOpen: (surveyId: number) => void;
  setSurveyListScrollPosition: (position: number) => void;
}

const apiSink = new ApiSink();

const defaultState: IPortalState = {
  loading: true,
  sink: { folders: [], orders: [] },
  surveys: [],
  layers: [],
  annotations: [],
  assets: [],
  selectedSurveyId: null,
  focusedSurveyId: null,
  selectedAnnotationId: null,
  focusedAnnotationId: null,
  selectedModelId: null,
  inspectorModel: null,
  inspectorShowMaterial: 0,
  openSurveyIds: [],
  selectedAssetId: null,
  focusedAssetIds: [],
  isCenterPaneCollapsed: true,
  surveyListFilters: defaultSurveyListFilters,
  undoArchiveQueue: [],
  allowAnnotationArrowKeyNavigation: true,
  surveyListExpansion: {},
  surveyListDownloadsOpen: {},
  surveyListScrollPosition: 0,
};

const defaultActions: ISinkActions = {
  resetUiState: () => {},
  unsetSelectedSurvey: () => {},
  setSelectedModel: () => {},
  setSelectedAsset: () => {},
  setCenterPaneCollapsed: () => {},
  setSurveySearchString: () => {},
  setSurveyDateRange: () => {},
  setSelectedTagIds: () => {},
  setSelectedFolderIds: () => {},
  setSelectedSiteIds: () => {},
  setSelectedStatuses: () => {},
  setSelectedOrderTypes: () => {},
  setSurveyDateType: () => {},
  setSelectedCreatedByUserIds: () => {},
  setSelectedLockedByUserIds: () => {},
  setShowOnlyArchived: () => {},
  stopInspecting: () => {},
  setAllowAnnotationArrowKeyNavigation: () => {},
  setSelectedSurveyId: () => {},
  resink: async () => {},
  getLayers: async () => {},
  toggleSurveyExpansion: () => {},
  expandAllSurveys: () => {},
  collapseAllSurveys: () => {},
  toggleSurveyListDownloadsOpen: () => {},
  closeSurveyListDownloadsOpen: () => {},
  setSurveyListScrollPosition: () => {},
};

const SinkStateContext = React.createContext<IPortalState>(defaultState);
const SinkActionsContext = React.createContext<ISinkActions>(defaultActions);

const resetState: Partial<IPortalState> = {
  focusedSurveyId: null,
  selectedModelId: null,
  inspectorShowMaterial: 0,
  openSurveyIds: [],
  selectedAssetId: null,
  focusedAssetIds: [],
  isCenterPaneCollapsed: true,
  surveyListFilters: defaultSurveyListFilters,
  allowAnnotationArrowKeyNavigation: true,
  surveyListExpansion: {},
  surveyListDownloadsOpen: {},
  surveyListScrollPosition: 0,
};

const SinkStore = ({ setState, getState, children }) => {
  const navigate = useNavigate();
  const state = getState();

  const { logout } = useAuthActions();

  const resetUiState = useCallback(() => {
    setState(resetState);
    Layer.resetAllVisibilities();
  }, [setState]);

  const unsetSelectedSurvey = useCallback(() => {
    UndoArchiveManager.clearQueue();
    setState((prev) => ({
      ...prev,
      selectedSurveyId: null,
    }));
  }, [setState]);

  const setCenterPaneCollapsed = useCallback(
    (isCollapsed: boolean): void => {
      setState((prev) => ({
        ...prev,
        isCenterPaneCollapsed: isCollapsed,
      }));
    },
    [setState]
  );

  const setSelectedModel = useCallback(
    (id: number | null) => {
      const expandCenterPane = !id;
      setCenterPaneCollapsed(expandCenterPane); // Expand the center pane if a model was clicked (and collapse if the model was reset)
      setState((prev) => ({
        ...prev,
        selectedModelId: id && Number(id),
      }));
    },
    [setState, setCenterPaneCollapsed]
  );

  const setSelectedSurveyId = useCallback(
    (id) => {
      setState((prev) => ({
        ...prev,
        selectedSurveyId: id,
      }));
    },
    [setState]
  );

  const getLayers = useCallback(
    (surveyId) => {
      return apiSink.getAnnotatedLayers(surveyId).then((response) => {
        if (response.error) {
          setState((prev) => ({
            ...prev,
            layers: [],
            annotations: [],
            inspectorModel: null,
          }));

          return;
        }

        const { inspectorModel } = getState();
        const annotations = response.data.annotations.map(
          (annotation) => new Annotation(annotation, getState, setState)
        );

        const match =
          inspectorModel &&
          annotations.find((annotation) => annotation.id === inspectorModel.id);

        setState((prev) => {
          return {
            ...prev,
            layers: response.data.layers.map(
              (layer) => new Layer(layer, getState, setState)
            ),
            annotations,
            inspectorModel: match || inspectorModel,
          };
        });
      });
    },
    [getState, setState]
  );

  const resink = useCallback(() => {
    return apiSink
      .getSink()
      .then(({ data: { sink } }) => {
        setState((prev) => ({
          ...prev,
          sink,
          surveys: sink.surveys.map(
            (survey) => new Survey(survey, getState, setState, navigate)
          ),
        }));
      })
      .catch((error) => {
        if (error.status === 401) {
          logout();
        }
      });
  }, [getState, setState, navigate, logout]);

  const stopInspecting = useCallback(() => {
    setState((prev) => ({
      ...prev,
      inspectorModel: null,
      inspectorShowMaterial: 0,
    }));
  }, [setState]);

  const toggleSurveyExpansion = useCallback(
    (surveyId: number) => {
      setState((prev) => ({
        ...prev,
        surveyListExpansion: {
          ...prev.surveyListExpansion,
          [surveyId]: !(prev.surveyListExpansion[surveyId] ?? false),
        },
      }));
    },
    [setState]
  );

  const expandAllSurveys = useCallback(
    (surveyIds: number[]) => {
      setState((prev) => ({
        ...prev,
        surveyListExpansion: {
          ...prev.surveyListExpansion,
          ...surveyIds.reduce((acc, id) => ({ ...acc, [id]: true }), {}),
        },
      }));
    },
    [setState]
  );

  const collapseAllSurveys = useCallback(() => {
    setState((prev) => ({
      ...prev,
      surveyListExpansion: {},
      surveyListDownloadsOpen: {},
    }));
  }, [setState]);

  const toggleSurveyListDownloadsOpen = useCallback(
    (surveyId: number) => {
      setState((prev) => ({
        ...prev,
        surveyListDownloadsOpen: {
          ...prev.surveyListDownloadsOpen,
          [surveyId]: !(prev.surveyListDownloadsOpen[surveyId] ?? false),
        },
      }));
    },
    [setState]
  );

  const closeSurveyListDownloadsOpen = useCallback(
    (surveyId: number) => {
      setState((prev) => {
        if (!prev.surveyListDownloadsOpen[surveyId]) {
          return prev;
        }
        const { [surveyId]: _removed, ...rest } = prev.surveyListDownloadsOpen;
        return {
          ...prev,
          surveyListDownloadsOpen: rest,
        };
      });
    },
    [setState]
  );

  const setSurveyListScrollPosition = useCallback(
    (position: number) => {
      setState((prev) => ({
        ...prev,
        surveyListScrollPosition: position,
      }));
    },
    [setState]
  );

  const setSelectedAsset = useCallback(
    (assetId: number) => {
      setState((prev) => ({
        ...prev,
        selectedAssetId: assetId,
      }));
    },
    [setState]
  );

  const setSurveySearchString = useCallback(
    (surveySearchString: string): void => {
      setState((prev) => ({
        ...prev,
        surveyListFilters: {
          ...prev.surveyListFilters,
          searchString: surveySearchString,
        },
      }));
    },
    [setState]
  );

  const setSurveyDateRange = useCallback(
    (d: { startDate?: string; endDate?: string }): void => {
      setState((prev) => ({
        ...prev,
        surveyListFilters: {
          ...prev.surveyListFilters,
          dateRange: {
            ...prev.surveyListFilters.dateRange,
            ...d,
          },
        },
      }));
    },
    [setState]
  );

  const setSelectedTagIds = useCallback((selectedTagIds: number[]): void => {
    setState((prev) => ({
      ...prev,
      surveyListFilters: {
        ...prev.surveyListFilters,
        selectedTagIds,
      },
    }));
  }, [setState]);

  const setSelectedFolderIds = useCallback(
    (selectedFolderIds: number[]): void => {
      setState((prev) => ({
        ...prev,
        surveyListFilters: {
          ...prev.surveyListFilters,
          selectedFolderIds,
        },
      }));
    },
    [setState]
  );

  const setSelectedSiteIds = useCallback((selectedSiteIds: number[]): void => {
    setState((prev) => ({
      ...prev,
      surveyListFilters: {
        ...prev.surveyListFilters,
        selectedSiteIds,
      },
    }));
  }, [setState]);

  const setSelectedStatuses = useCallback(
    (selectedStatuses: string[]): void => {
      setState((prev) => ({
        ...prev,
        surveyListFilters: {
          ...prev.surveyListFilters,
          selectedStatuses,
        },
      }));
    },
    [setState]
  );

  const setSelectedOrderTypes = useCallback(
    (selectedOrderTypes: string[]): void => {
      setState((prev) => ({
        ...prev,
        surveyListFilters: {
          ...prev.surveyListFilters,
          selectedOrderTypes,
        },
      }));
    },
    [setState]
  );

  const setSurveyDateType = useCallback(
    (surveyDateType: SurveyDateType): void => {
      setState((prev) => ({
        ...prev,
        surveyListFilters: {
          ...prev.surveyListFilters,
          dateType: surveyDateType,
        },
      }));
    },
    [setState]
  );

  const setSelectedCreatedByUserIds = useCallback(
    (selectedCreatedByUserIds: number[]): void => {
      setState((prev) => ({
        ...prev,
        surveyListFilters: {
          ...prev.surveyListFilters,
          selectedCreatedByUserIds,
        },
      }));
    },
    [setState]
  );

  const setSelectedLockedByUserIds = useCallback(
    (selectedLockedByUserIds: number[]): void => {
      setState((prev) => ({
        ...prev,
        surveyListFilters: {
          ...prev.surveyListFilters,
          selectedLockedByUserIds,
        },
      }));
    },
    [setState]
  );

  const setShowOnlyArchived = useCallback((showOnlyArchived: boolean): void => {
    setState((prev) => ({
      ...prev,
      surveyListFilters: {
        ...prev.surveyListFilters,
        showOnlyArchived,
      },
    }));
  }, [setState]);

  const setAllowAnnotationArrowKeyNavigation = useCallback(
    (allowAnnotationArrowKeyNavigation: boolean) => {
      setState((prev) => ({
        ...prev,
        allowAnnotationArrowKeyNavigation,
      }));
    },
    [setState]
  );

  const actions = useMemo<ISinkActions>(
    () => ({
      resetUiState,
      unsetSelectedSurvey,
      setSelectedModel,
      setSelectedAsset,
      setCenterPaneCollapsed,
      setSurveySearchString,
      setSurveyDateRange,
      setSelectedTagIds,
      setSelectedFolderIds,
      setSelectedSiteIds,
      setSelectedStatuses,
      setSelectedOrderTypes,
      setSurveyDateType,
      setSelectedCreatedByUserIds,
      setSelectedLockedByUserIds,
      setShowOnlyArchived,
      stopInspecting,
      setAllowAnnotationArrowKeyNavigation,
      setSelectedSurveyId,
      resink,
      getLayers,
      toggleSurveyExpansion,
      expandAllSurveys,
      collapseAllSurveys,
      toggleSurveyListDownloadsOpen,
      closeSurveyListDownloadsOpen,
      setSurveyListScrollPosition,
    }),
    [
      resetUiState,
      unsetSelectedSurvey,
      setSelectedModel,
      setSelectedAsset,
      setCenterPaneCollapsed,
      setSurveySearchString,
      setSurveyDateRange,
      setSelectedTagIds,
      setSelectedFolderIds,
      setSelectedSiteIds,
      setSelectedStatuses,
      setSelectedOrderTypes,
      setSurveyDateType,
      setSelectedCreatedByUserIds,
      setSelectedLockedByUserIds,
      setShowOnlyArchived,
      stopInspecting,
      setAllowAnnotationArrowKeyNavigation,
      setSelectedSurveyId,
      resink,
      getLayers,
      toggleSurveyExpansion,
      expandAllSurveys,
      collapseAllSurveys,
      toggleSurveyListDownloadsOpen,
      closeSurveyListDownloadsOpen,
      setSurveyListScrollPosition,
    ]
  );

  const refreshSink = React.useCallback(() => {
    Survey.connect(getState, setState);
    Layer.connect(getState, setState);
    Asset.connect(getState, setState);
    Annotation.connect(getState, setState);
    AdHoc.connect(getState, setState);
    Cannot.connect(getState, setState);

    ApiLayers.connect(getState, setState);
    ApiAnnotations.connect(getState, setState);

    UndoArchiveManager.connect(getState, setState);

    apiSink
      .getSink()
      .then((response) => {
        const { sink } = response.data;

        setState((prev) => ({
          ...prev,
          sink,
          surveys: sink.surveys.map(
            (survey) => new Survey(survey, getState, setState, navigate)
          ),
          loading: false,
        }));
      })
      .catch((error) => {
        if (error.status === 401) {
          logout();
        }
      });
  }, [getState, setState, logout, navigate]);

  useRefreshOnFocus(refreshSink);

  return (
    <SinkStateContext.Provider value={state}>
      <SinkActionsContext.Provider value={actions}>
        {state.loading ? <Loading /> : children}
      </SinkActionsContext.Provider>
    </SinkStateContext.Provider>
  );
};

export class SinkProvider extends React.Component {
  state = { ...defaultState };
  getState = () => this.state;
  sinkSetState = this.setState.bind(this);
  render() {
    return (
      <SinkStore getState={this.getState} setState={this.sinkSetState}>
        {this.props.children}
      </SinkStore>
    );
  }
}

export const useSinkState = () => {
  const context = React.useContext(SinkStateContext);
  if (context === undefined) {
    throw new Error("Must 'useSinkState' within a SinkProvider.");
  }
  return context;
};

export const useSinkActions = () => {
  const context = React.useContext(SinkActionsContext);
  if (context === undefined) {
    throw new Error("Must 'useSinkActions' within a SinkProvider.");
  }
  return context;
};
